import { Flex } from '@televet/kibble-ui';
import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ChannelList from './components/ChannelList';
import ChannelView from './components/ChannelView';
import { FeatureFlagName } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { useInAppNotificationsProvider } from 'shared/providers/InAppNotificationsProvider';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { PermissionType } from 'shared/types/graphql';
import { TeletriageConversationCenter } from './components/TeletriageConversationCenter';
import TopToolbar from './components/TopToolbar';
import { ConversationsContextProvider } from './context/ConversationsContext';
import removeExpiredConversationDrafts from './utils/removeExpiredConversationDrafts';
import ChannelViewOld from './components/ChannelViewOld';

const Conversations = (): JSX.Element => {
  const { channelId } = useParams<{ channelId: string }>();
  const { isFeatureEnabled, isFeatureFlagsLoading } = useFeatureFlag();
  const { userHasPermission } = useClinicUser();
  const { isMobile } = useResolutionProvider();
  const { setExcludedChannelIds } = useInAppNotificationsProvider();

  // On initial page load, check for and remove expired draft messages in local storage
  useEffect(() => {
    removeExpiredConversationDrafts();
  }, []);

  // Ensure user doesn't get notifications for the current channel
  useEffect(() => {
    if (channelId) setExcludedChannelIds([channelId]);
    return (): void => setExcludedChannelIds([]);
  }, [channelId, setExcludedChannelIds]);

  const showChannelList = !isMobile || !channelId;
  const showChannelView = !isMobile || !!channelId;

  const shouldShowTeletriageConversationCenter = useMemo(
    () => userHasPermission(PermissionType.ManageTeletriage) && isFeatureEnabled(FeatureFlagName.CarePlans),
    [isFeatureEnabled, userHasPermission],
  );

  const hasConversationsPerformanceImprovements = useMemo(
    () => isFeatureEnabled(FeatureFlagName.ConversationsPerformanceImprovements) && !isFeatureFlagsLoading,
    [isFeatureEnabled, isFeatureFlagsLoading],
  );

  return (
    <Flex height="100%" direction="column">
      <ConversationsContextProvider shouldShowTeletriageConversationCenter={shouldShowTeletriageConversationCenter}>
        {(!isMobile || !channelId) && (
          <TopToolbar shouldShowTeletriageConversationCenter={shouldShowTeletriageConversationCenter} />
        )}
        <Flex
          className="Conversations__Container"
          w="100%"
          h="100%"
          overflow="auto"
          maxW="100%"
          bgColor="background.subtle"
        >
          {showChannelList ? <ChannelList /> : null}
          {showChannelView ? (
            hasConversationsPerformanceImprovements ? (
              <ChannelView channelId={channelId} />
            ) : (
              <ChannelViewOld channelId={channelId} />
            )
          ) : null}
          {shouldShowTeletriageConversationCenter && <TeletriageConversationCenter />}
        </Flex>
      </ConversationsContextProvider>
    </Flex>
  );
};

export default Conversations;
